import { GetServerSideProps } from 'next';
import Head from 'next/head';
import { APP_URL } from 'src/logic/config/meta/meta.config';
import PageMetaHead from 'src/logic/config/meta/PageMetaHead';
import { BlogPost } from 'src/logic/models/blog';
import { getBlogPost } from 'src/logic/services/blog';
import { sm } from 'src/site-map';
import BlogPostPageContent from 'src/ui/components/smart/landing/blog/BlogPostPageContent';
import LandingLayout from 'src/ui/components/smart/landing/layout/LandingLayout';

type IProps = {
  post: BlogPost | null;
};

function formatToISO(dateStr: string): string {
  // Split into [day, month, year]
  const [day, month, year] = dateStr.split('-').map(Number);

  // Create Date in UTC (adjust if you want a specific timezone)
  const date = new Date(Date.UTC(year, month - 1, day));

  return date.toISOString(); // Gives "2025-08-07T00:00:00.000Z"
}

const BlogPostPage = ({ post }: IProps) => {
  if (!post) return null;

  const url = `${APP_URL}${sm.blog.post(post.slug).url}`;
  const publishTime = post.published_at ? formatToISO(post.published_at) : undefined;

  return (
    <>
      <PageMetaHead
        page_meta={{
          title: `${post.seo?.title || post?.title} | eProcurement`,
          description: post.seo?.description,
          keywords: post.seo?.keywords ? post.seo.keywords.join(', ') : undefined,
          thumbnail: post.cover_photo_path,
        }}
        useExactTitle
      />
      <Head>
        <link rel='canonical' href={url} />

        <meta key='og:url' property='og:url' content={url} />
        <meta key='og:type' property='og:type' content='article' />
        {publishTime && <meta key='article:published_time' content={publishTime} />}
        {post?.tags?.map(tag => (
          <meta key={tag.slug} property='article:tag' content={tag.name} />
        ))}
        <meta property='article:author' content={'eProcurement'} />

        {/* Structured Data (JSON-LD for Article) */}
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'Article',
              headline: post.seo?.title || post?.title,
              description: post.seo?.description,
              image: post.cover_photo_path ? [post.cover_photo_path] : [],
              datePublished: publishTime,
              mainEntityOfPage: {
                '@type': 'WebPage',
                '@id': url,
              },
              keywords: post.seo?.keywords?.join(', '),
            }),
          }}
        />
      </Head>
      <BlogPostPageContent post={post} />
    </>
  );
};

export const getServerSideProps: GetServerSideProps = async context => {
  const slug = context.params?.slug as string;
  let post: BlogPost | null = null;

  try {
    const response = await getBlogPost(slug);
    post = response?.data?.data;
  } catch (error) {
    if (error?.response?.status === 404) {
      return {
        notFound: true,
      };
    }
    console.error('Unexpected error while fetching blog post:', error);
    return {
      redirect: {
        destination: '/500',
        permanent: false,
      },
    };
  }

  return {
    props: {
      post,
    },
  };
};

BlogPostPage.getLayout = function getLayout(page: React.ReactElement) {
  return <LandingLayout>{page}</LandingLayout>;
};

export default BlogPostPage;
